/**
 * Basemap Token Interceptor
 *
 * Intercepts network requests to ArcGIS basemap services and injects
 * the session token. This allows the ArcGIS JS API to use session-based
 * authentication instead of per-tile API key authentication.
 *
 * Supports both fetch() and XMLHttpRequest for compatibility with the ArcGIS JS API.
 */

import { debugLog, debugWarn } from './basemap-debug.util';

let sessionToken: string | null = null;
let interceptorsInitialized = false;

/**
 * Checks if a URL is a basemap-related request that needs token injection
 */
function isBasemapRequest(url: string): boolean {
  if (typeof url !== 'string') {
    return false;
  }

  return (
    url.indexOf('basemaps-api.arcgis.com') !== -1 ||
    url.indexOf('ibasemaps-api.arcgis.com') !== -1 ||
    url.indexOf('basemapstyles-api.arcgis.com') !== -1
  );
}

/**
 * Adds or replaces the token parameter in a URL
 */
function addOrReplaceToken(url: string, token: string): string {
  try {
    // Try to use URL API if available
    if (typeof URL !== 'undefined') {
      const urlObj = new URL(url, window.location.href);
      urlObj.searchParams.set('token', token);
      return urlObj.toString();
    }
  } catch (e) {
    // Fallback to string manipulation
  }

  // Fallback: string-based manipulation
  const hasQuery = url.indexOf('?') !== -1;
  const hasToken = /([?&])token=/.test(url);

  if (hasToken) {
    // Replace existing token
    return url.replace(
      /([?&])token=[^&]*/,
      '$1token=' + encodeURIComponent(token)
    );
  } else {
    // Add new token
    return url + (hasQuery ? '&' : '?') + 'token=' + encodeURIComponent(token);
  }
}

/**
 * Sets the session token to be used for basemap requests
 */
export function setBasemapSessionToken(token: string | null): void {
  sessionToken = token;
  debugLog('Basemap session token updated');
}

/**
 * Gets the current session token
 */
export function getBasemapSessionToken(): string | null {
  return sessionToken;
}

/**
 * Initializes network interceptors for fetch and XMLHttpRequest
 * Should be called once during app initialization
 */
export function initializeBasemapTokenInterceptors(): void {
  if (interceptorsInitialized) {
    debugWarn('Basemap token interceptors already initialized');
    return;
  }

  // Intercept fetch requests
  const originalFetch = window.fetch;
  window.fetch = function (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    let url: string | undefined;

    if (typeof input === 'string') {
      url = input;
    } else if (input instanceof Request) {
      url = input.url;
    } else if (input instanceof URL) {
      url = input.toString();
    }

    const isBasemap = typeof url === 'string' && isBasemapRequest(url);

    if (isBasemap && sessionToken && url) {
      const updatedUrl = addOrReplaceToken(url, sessionToken);

      if (typeof input === 'string') {
        return originalFetch(updatedUrl, init);
      } else if (input instanceof Request) {
        const updatedRequest = new Request(updatedUrl, input);
        return originalFetch(updatedRequest, init);
      } else if (input instanceof URL) {
        return originalFetch(updatedUrl, init);
      }
    }

    // Convert URL to string if needed for the original fetch call
    if (input instanceof URL) {
      return originalFetch(input.toString(), init);
    }

    return originalFetch(input, init);
  };

  // Intercept XMLHttpRequest
  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (
    method: string,
    url: string | URL,
    async?: boolean,
    username?: string | null,
    password?: string | null
  ): void {
    const urlString = typeof url === 'string' ? url : url.toString();
    let finalUrl = urlString;

    const isBasemap = isBasemapRequest(urlString);

    if (isBasemap && sessionToken) {
      finalUrl = addOrReplaceToken(urlString, sessionToken);
    }

    // Call original with appropriate arguments
    if (async !== undefined) {
      if (username !== undefined && password !== undefined) {
        originalOpen.call(this, method, finalUrl, async, username, password);
      } else if (username !== undefined) {
        originalOpen.call(this, method, finalUrl, async, username);
      } else {
        originalOpen.call(this, method, finalUrl, async);
      }
    } else {
      // Sync mode - default async is true per spec
      originalOpen.call(this, method, finalUrl, true);
    }
  };

  interceptorsInitialized = true;
  debugLog('Basemap token interceptors initialized');
}

/**
 * Checks if interceptors are initialized
 */
export function areInterceptorsInitialized(): boolean {
  return interceptorsInitialized;
}
